import React, { useCallback, useEffect, useMemo, useState } from "react";
import SelectMenu from "pages/flyer/builder/components/Inspector/components/SelectMenu";
import { getAllowedFormats } from "pages/flyer/builder/components/SideBar/Aside/components";
import { useStore } from "../../../store";
import { StoreType } from "../../../store/types";
import { FORMATS } from "../../flyer/builder/constants";
import { getFlyerSizeDisplay, displayInInch } from "../../flyer/builder/utils";
import { updateCampaignExtraData } from "../../../graphQL";
import { DMDocumentFormats, ADMDocumentFormats } from "../../../shared/constants";

interface FormatSelectorProps {
  onFormatChange?: (format: any) => void;
  selectMode?: boolean;
  value?: string; // Add controlled value prop
}

const FormatSelector: React.FC<FormatSelectorProps> = ({ onFormatChange, selectMode = false, value }) => {
  const {
    campaign,
    costsCalculationData: { printingCosts },
    country,
    updateCampaign,
    measurementUnit,
  }: StoreType = useStore();
  const { id: campaignId, channel, flyerType, subtype, flyerWeight } = campaign;

  const flyerTypes = useMemo(
    () =>
      Object.entries(FORMATS).filter(([key]) => {
        const allowedFormats = getAllowedFormats({
          channel,
          subtype,
          countryCode: country.code,
          printingCosts,
          flyerWeight,
          isPublicBuilder: !(channel || subtype),
        });
        return allowedFormats.includes(key);
      }),
    [channel, subtype, country.code, printingCosts, flyerWeight, selectMode]
  );

  // Use controlled value or fallback to flyerType from campaign or first available format
  const selectedFlyerType = value || flyerType || flyerTypes[0]?.[0] || "TEARAWAY";

  const currentFormat = FORMATS[selectedFlyerType as keyof typeof FORMATS];

  const handleFlyerTypeChange = useCallback(
    async (newFormat: any) => {
      if (!selectMode) {
        await updateCampaignExtraData({
          campaignId,
          flyerType: newFormat.format,
        });
      }
      updateCampaign({ flyerType: newFormat.format });
      // Call onFormatChange to notify parent of the change
      onFormatChange?.(newFormat.format);
    },
    [selectMode, updateCampaign, campaignId, onFormatChange]
  );

  const flyerTypeOptions = useMemo(
    () =>
      flyerTypes.map(([, value]) => {
        return {
          label: value.name,
          helperText: value.price,
          format: value.format,
          isChecked: selectedFlyerType === value.format,
          action: () => {
            handleFlyerTypeChange(value);
          },
        };
      }),
    [flyerTypes, selectedFlyerType, handleFlyerTypeChange]
  );

  const getDisplaySize = (format: any) => {
    // Check if format is USPS compliant (ADM/EDDM)
    const isUspsCompliant =
      country.code === "US" &&
      format.format &&
      [...Object.values(DMDocumentFormats), ...Object.values(ADMDocumentFormats)].includes(format.format);

    // For USPS compliant formats (ADM/EDDM), display as Height x Width (USPS standard)
    // For other formats, display as Width x Height (standard)
    if (isUspsCompliant) {
      return getFlyerSizeDisplay(format.heightInMm, format.widthInMm, displayInInch(country.code, measurementUnit));
    }

    return getFlyerSizeDisplay(format.widthInMm, format.heightInMm, displayInInch(country.code, measurementUnit));
  };

  // Removed the problematic useEffect that was causing circular updates
  // Now the component only notifies about format changes when user actually changes it

  return (
    <SelectMenu
      containerStyle={{
        height: 75,
        minWidth: 230,
      }}
      items={flyerTypeOptions}
      displayValueStyle={{
        width: 205,
      }}
      displayValue={
        <div className="flex items-center gap-2">
          <Icon />
          <div className="flex flex-col">
            <div className="text-grey-dark dark:text-muted-foreground text-xs">({getDisplaySize(currentFormat)})</div>
            <div className="text-blue-deep-dark dark:text-foreground">{currentFormat?.name}</div>
            <div className="text-grey-dark dark:text-muted-foreground text-xs">{currentFormat?.price}</div>
          </div>
        </div>
      }
      className="w-full p-3 border border-light-blue dark:border-border rounded-lg bg-white dark:bg-card text-blue-deep-dark dark:text-foreground text-sm appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:border-secondary-deep-blue dark:hover:border-border hover:shadow-md hover:scale-105 transform"
    />
  );
};

const Icon = () => (
  <svg width="38" height="30" viewBox="0 0 38 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_d_5798_26619)">
      <path
        d="M4.12305 4.7456C4.12305 3.64103 5.01848 2.74561 6.12305 2.74561H27.6416C28.1723 2.74561 28.6812 2.95651 29.0563 3.33188L33.3457 7.62412V21.0264C33.3457 22.1309 32.4502 23.0264 31.3457 23.0264H6.12305C5.01848 23.0264 4.12305 22.1309 4.12305 21.0264V4.7456Z"
        fill="white"
      />
      <path
        d="M5.93164 7.5459C5.93164 6.44133 6.82707 5.5459 7.93164 5.5459H14.0136C15.1182 5.5459 16.0136 6.44133 16.0136 7.5459V15.2944H7.93164C6.82707 15.2944 5.93164 14.399 5.93164 13.2944V7.5459Z"
        fill="#4F5B94"
      />
      <ellipse cx="10.9728" cy="9.28002" rx="2.507" ry="2.51" fill="white" />
      <path d="M5.93164 11.6851L16.0136 14.6286V15.2675H5.93164V11.6851Z" fill="#AEAFC7" />
      <path
        d="M16.0137 10.624L5.93173 14.7419C5.93173 15.5116 6.55574 16.1356 7.3255 16.1356H14.0137C15.1182 16.1356 16.0137 15.2402 16.0137 14.1356V10.624Z"
        fill="#E2E2FC"
      />
      <path
        d="M4.12305 19.4087H33.3457V21.0264C33.3457 22.131 32.4502 23.0264 31.3457 23.0264H6.12305C5.01848 23.0264 4.12305 22.131 4.12305 21.0264V19.4087Z"
        fill="#E2E2FC"
      />
      <rect x="26.1582" y="16.9839" width="5.23069" height="5.17064" rx="1" fill="#E2E2FC" />
      <rect x="8.03125" y="21.0083" width="15.0124" height="0.703053" fill="white" />
      <path
        d="M28.2061 19.8037C28.4102 19.8037 28.5762 19.9694 28.5762 20.1738V21.21C28.5761 21.4143 28.4102 21.5801 28.2061 21.5801H27.1719C26.9678 21.5801 26.8018 21.4143 26.8018 21.21V20.1738C26.8018 19.9694 26.9677 19.8037 27.1719 19.8037H28.2061ZM29.9561 19.8037C30.0649 19.8037 30.1533 19.892 30.1533 20.001V20.1992H30.5479C30.6566 20.1993 30.745 20.2876 30.7451 20.3965C30.7451 20.5054 30.6567 20.5937 30.5479 20.5938H30.1533V21.3828C30.1532 21.4917 30.0648 21.5801 29.9561 21.5801H29.167C29.0584 21.5798 28.9708 21.4915 28.9707 21.3828C28.9707 21.274 29.0583 21.1858 29.167 21.1855H29.7588V20.001C29.7588 19.892 29.8472 19.8037 29.9561 19.8037ZM30.5479 20.79C30.6564 20.7903 30.7439 20.8787 30.7441 20.9873V21.3828C30.744 21.4915 30.6564 21.5798 30.5479 21.5801C30.4391 21.5801 30.3507 21.4917 30.3506 21.3828V20.9873C30.3508 20.8785 30.4391 20.79 30.5479 20.79ZM27.1963 21.1855H28.1816V20.1982H27.1963V21.1855ZM29.167 19.8037C29.2759 19.8037 29.3643 19.892 29.3643 20.001V20.5928C29.3643 20.7018 29.2759 20.79 29.167 20.79C29.0583 20.7898 28.9707 20.7016 28.9707 20.5928V20.001C28.9707 19.8921 29.0583 19.804 29.167 19.8037ZM28.2061 17.6328C28.4102 17.6328 28.5762 17.7985 28.5762 18.0029V19.0391C28.576 19.2433 28.4101 19.4092 28.2061 19.4092H27.1719C26.9678 19.4092 26.8019 19.2433 26.8018 19.0391V18.0029C26.8018 17.7985 26.9677 17.6328 27.1719 17.6328H28.2061ZM30.375 17.6328C30.5791 17.6328 30.7441 17.7986 30.7441 18.0029V19.0391C30.744 19.2433 30.579 19.4092 30.375 19.4092H29.3398C29.1358 19.4091 28.9708 19.2433 28.9707 19.0391V18.0029C28.9707 17.7986 29.1358 17.6329 29.3398 17.6328H30.375ZM27.1963 19.0137H28.1816V18.0273H27.1963V19.0137ZM29.3643 19.0137H30.3506V18.0273H29.3643V19.0137Z"
        fill="#4F5B94"
      />
      <rect x="18.6758" y="7.18408" width="11.5892" height="2.206" fill="#E2E2FC" />
      <rect x="18.6758" y="10.8203" width="8.54638" height="0.868406" fill="#E2E2FC" />
      <rect x="18.6758" y="12.3027" width="9.88872" height="0.868406" fill="#E2E2FC" />
      <rect x="18.6758" y="15.2671" width="9.88872" height="0.868406" fill="#E2E2FC" />
      <rect x="18.6758" y="13.7847" width="8.99397" height="0.868406" fill="#E2E2FC" />
      <path
        d="M28.4521 2.64062L33.4318 7.62623H30.4521C29.3476 7.62623 28.4521 6.7308 28.4521 5.62623V2.64062Z"
        fill="#4F5B94"
      />
    </g>
    <defs>
      <filter
        id="filter0_d_5798_26619"
        x="0.123047"
        y="0.640625"
        width="37.3086"
        height="28.3857"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="2" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.0745098 0 0 0 0 0.0588235 0 0 0 0 0.14902 0 0 0 0.1 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5798_26619" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5798_26619" result="shape" />
      </filter>
    </defs>
  </svg>
);

export default FormatSelector;
